import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { isAppUnavailablePayload, toastApiError } from "helpers/apiErrorToast";
import { translationsWithFallback } from "helpers/utils";
import { t } from "i18next";
import { toast } from "react-toastify";

import type { TFetchStatus } from "./types";

export const fetchIntegrations = createAsyncThunk("settings/getIntegrations", async (_, { rejectWithValue }) => {
    try {
        const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + "/integrations");
        // A 200 without the expected envelope (e.g. an HTML page from a proxy) must not reach the reducer.
        if (!Array.isArray(response.data?.data)) return rejectWithValue(response.data);
        return response.data.data as IIntegrations[];
    } catch (error: any) {
        return rejectWithValue(error.response?.data);
    }
});

export const GOOGLE_REAUTH_REQUIRED = "ServerError.GoogleReauthRequired";

/** Whether a rejected payload (`error.response.data`) is the backend telling us Google needs a reauth. */
export const isGoogleReauthRequired = (payload: unknown) =>
    (payload as { message?: string } | undefined)?.message === GOOGLE_REAUTH_REQUIRED;

/**
 * Google rejected the stored credentials: the backend has already flagged the integration as
 * no longer connected, so reload the integrations for the settings badge to reflect it.
 */
export const refreshIntegrationsOnGoogleReauthRequired = (
    error: { response?: { data?: { message?: string } } },
    dispatch: (action: ReturnType<typeof fetchIntegrations>) => unknown,
) => {
    if (isGoogleReauthRequired(error?.response?.data)) dispatch(fetchIntegrations());
};

/**
 * Error toast for the Google-backed thunks. The reconnect message gets its own toastId so that a
 * generic error toast already on screen does not swallow it; other errors keep the generic one.
 */
export const toastGoogleBackedError = (payload: unknown) => {
    const message = (payload as { message?: string } | undefined)?.message;
    if (isAppUnavailablePayload(payload)) return;
    const translation = translationsWithFallback(message ?? "", "ServerError.InternalServerError");
    const toastId = isGoogleReauthRequired(payload) ? GOOGLE_REAUTH_REQUIRED : "ServerError.InternalServerError";
    toast.error(`${translation}`, { toastId });
};

export const createIntegration = createAsyncThunk(
    "settings/postIntegration",
    async (partner: { partner: string }, { rejectWithValue }) => {
        try {
            const response = await axios.post(import.meta.env.REACT_APP_API_ENDPOINT + "/integrations", partner);
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const deleteIntegration = createAsyncThunk(
    "settings/deleteIntegration",
    async (id: number, { rejectWithValue }) => {
        try {
            const response = await axios.delete(import.meta.env.REACT_APP_API_ENDPOINT + `/integrations/${id}`);
            return response;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const authGoogleAccount = createAsyncThunk("settings/authGoogleAccount", async (_, { rejectWithValue }) => {
    try {
        const response = await axios.post(import.meta.env.REACT_APP_API_ENDPOINT + "/integrations/google/auth");
        window.location.href = response.data.data;
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

export const authBexioAccount = createAsyncThunk("settings/authBexioAccount", async (_, { rejectWithValue }) => {
    try {
        const response = await axios.post(import.meta.env.REACT_APP_API_ENDPOINT + "/integrations/bexio/auth");

        window.location.href = response.data.data;
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

export const authGoogleCallback = createAsyncThunk(
    "settings/authGoogleCallback",
    async (code: string, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_ENDPOINT + `/integrations/google/auth/callback?code=${code}`,
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const authBexioCallback = createAsyncThunk(
    "settings/authBexioCallback",
    async (code: string, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_ENDPOINT + `/integrations/bexio/auth/callback?code=${code}`,
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const connectDocoon = createAsyncThunk(
    "settings/connectDocoon",
    async (data: { login: string; pwd: string; docoon_url: string }, { rejectWithValue }) => {
        try {
            const response = await axios.post(
                import.meta.env.REACT_APP_API_ENDPOINT + "/integrations/docoon/connect",
                data,
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export type DocoonConfigItemType = "service" | "document_type" | "template";

export interface DocoonConfigItem {
    id: number;
    companyId: number;
    type: DocoonConfigItemType;
    value: string;
}

export const fetchDocoonConfig = createAsyncThunk("settings/fetchDocoonConfig", async (_, { rejectWithValue }) => {
    try {
        const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + "/integrations/docoon/config");
        return response.data.data as DocoonConfigItem[];
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

export const createDocoonConfigItem = createAsyncThunk(
    "settings/createDocoonConfigItem",
    async (payload: { type: DocoonConfigItemType; value: string }, { rejectWithValue }) => {
        try {
            const response = await axios.post(
                import.meta.env.REACT_APP_API_ENDPOINT + "/integrations/docoon/config",
                payload,
            );
            return response.data.data as DocoonConfigItem;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const deleteDocoonConfigItem = createAsyncThunk(
    "settings/deleteDocoonConfigItem",
    async (id: number, { rejectWithValue }) => {
        try {
            await axios.delete(import.meta.env.REACT_APP_API_ENDPOINT + `/integrations/docoon/config/${id}`);
            return id;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const askIntegration = createAsyncThunk(
    "settings/ask-integration",
    async (integrationName: { integrationName: string }, { rejectWithValue }) => {
        try {
            const response = await axios.post(
                import.meta.env.REACT_APP_API_ENDPOINT + "/integrations/ask-integration",
                integrationName,
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export interface IIntegrations {
    id: number;
    companyId: number;
    partner: string;
    connectedBy: number;
    data: string;
    status: number;
    /**
     * Email of the connected Google account (null for other partners, or integrations connected before it was stored)
     */
    accountEmail?: string | null;
}

export interface IntegrationsState {
    integrations: IIntegrations[];
    authGoogleStatus: TFetchStatus;
    authBexioStatus: TFetchStatus;
    /**
     * Whether the company has a Bexio active integration
     */
    hasBexioIntegration: boolean;
    /**
     * Whether the company has a Docoon active integration
     */
    hasDocoonIntegration: boolean;
    docoonConfig: DocoonConfigItem[];
}

const initialState: IntegrationsState = {
    integrations: [],
    authGoogleStatus: "idle",
    authBexioStatus: "idle",
    hasBexioIntegration: false,
    hasDocoonIntegration: false,
    docoonConfig: [],
};

export enum IntegrationStatus {
    CONNECTED = 2,
    PENDING = 1,
}

export const integrationsSlice = createSlice({
    name: "integrations",
    initialState,
    reducers: {},
    extraReducers(builder) {
        builder.addCase(fetchIntegrations.fulfilled, (state, action) => {
            state.integrations = action.payload;
            state.hasBexioIntegration = action.payload.some(
                (integration) => integration.partner === "bexio" && integration.status === IntegrationStatus.CONNECTED,
            );
            state.hasDocoonIntegration = action.payload.some(
                (integration) => integration.partner === "docoon" && integration.status === IntegrationStatus.CONNECTED,
            );
        });
        builder.addCase(fetchIntegrations.rejected, (state, { payload }) => {
            toastApiError(payload, "ServerError.InternalServerError");
        });
        builder.addCase(createIntegration.rejected, (state, { payload }) => {
            toastApiError(payload, "ServerError.InternalServerError");
        });
        builder.addCase(authGoogleAccount.pending, (state) => {
            state.authGoogleStatus = "pending";
        });
        builder.addCase(authGoogleAccount.rejected, (state) => {
            state.authGoogleStatus = "failed";
        });

        builder.addCase(authBexioAccount.pending, (state) => {
            state.authBexioStatus = "pending";
        });
        builder.addCase(authBexioAccount.rejected, (state) => {
            state.authBexioStatus = "failed";
        });
        builder.addCase(authGoogleCallback.fulfilled, (state, action) => {
            toast.success(`${t("IntegrationsSettings.SuccessfullyCreated")}`);
            state.authGoogleStatus = "succeeded";
        });
        builder.addCase(authGoogleCallback.rejected, (state, { payload }) => {
            state.authGoogleStatus = "failed";
            toastApiError(payload, "ServerError.InternalServerError");
        });
        builder.addCase(deleteIntegration.fulfilled, (state, action) => {
            state.integrations = state.integrations.filter((integration) => integration.id !== action.meta.arg);
            toast.success(`${t("IntegrationsSettings.SuccessfullyDeleted")}`);
        });
        builder.addCase(deleteIntegration.rejected, (state, { payload }) => {
            toastApiError(payload, "ServerError.InternalServerError");
        });
        builder.addCase(connectDocoon.fulfilled, (state) => {
            state.hasDocoonIntegration = true;
            toast.success(`${t("IntegrationsSettings.SuccessfullyCreated")}`);
        });
        builder.addCase(connectDocoon.rejected, (_state, { payload }) => {
            toastApiError(payload, "DocoonConnect.Error");
        });
        builder.addCase(fetchDocoonConfig.fulfilled, (state, action) => {
            state.docoonConfig = action.payload;
        });
        builder.addCase(createDocoonConfigItem.fulfilled, (state, action) => {
            state.docoonConfig = [...state.docoonConfig, action.payload].sort((a, b) => {
                if (a.type !== b.type) return a.type.localeCompare(b.type);
                return a.value.localeCompare(b.value);
            });
            toast.success(`${t("DocoonSettings.ItemAdded")}`);
        });
        builder.addCase(createDocoonConfigItem.rejected, (_state, { payload }) => {
            toastApiError(payload, "DocoonConfig.CreateError");
        });
        builder.addCase(deleteDocoonConfigItem.fulfilled, (state, action) => {
            state.docoonConfig = state.docoonConfig.filter((i) => i.id !== action.payload);
            toast.success(`${t("DocoonSettings.ItemDeleted")}`);
        });
        builder.addCase(deleteDocoonConfigItem.rejected, (_state, { payload }) => {
            toastApiError(payload, "DocoonConfig.DeleteError");
        });
        builder.addCase(askIntegration.fulfilled, (_state, _action) => {
            toast.success(`${t("IntegrationsSettings.RequestTaken")}`);
        });
        builder.addCase(askIntegration.rejected, (_state, { payload }) => {
            toastApiError(payload, "ServerError.InternalServerError");
        });
    },
});

export default integrationsSlice.reducer;
